import {
  Activity,
  ArrowLeft,
  CheckCircle2,
  Database,
  Globe,
  RefreshCw,
  Server,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { Link, Navigate } from "react-router-dom";

import { PageContainer } from "@/components/PageContainer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAdminSistema } from "@/hooks/useAdminSistema";
import type {
  CloudRunStatus,
  CloudSqlStatus,
  SistemaStatus,
  SslCert,
} from "@/hooks/useAdminSistema";
import { useMe } from "@/hooks/useMe";
import { isMaitimeStaff } from "@/lib/nav-config";
import { cn } from "@/lib/utils";

function formatDataOra(iso?: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("it-IT", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function AdminSistemaPage() {
  const { data: me, isLoading: loadingMe } = useMe();
  const { data, isLoading, refetch, isFetching } = useAdminSistema();

  if (!loadingMe && me && !isMaitimeStaff(me)) {
    return <Navigate to="/" replace />;
  }

  return (
    <PageContainer>
      <Link
        to="/admin"
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors w-fit"
      >
        <ArrowLeft className="size-3.5" />
        Backoffice
      </Link>

      <header className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="space-y-1">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            Backoffice Maitime
          </p>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">
            Stato sistema
          </h1>
          <p className="text-sm text-muted-foreground max-w-2xl pt-1">
            Snapshot dei servizi GCP per chi non ha accesso diretto alla Console: Cloud Run, Cloud SQL, certificati SSL, versione DB. Auto-refresh ogni 60s.
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={cn("size-3.5", isFetching && "animate-spin")} />
          Aggiorna
        </Button>
      </header>

      {isLoading ? (
        <Skeleton className="h-96 w-full" />
      ) : !data ? (
        <div className="rounded-lg border bg-destructive/10 p-4 text-sm text-destructive">
          Errore caricamento stato sistema.
        </div>
      ) : (
        <SistemaContent data={data} />
      )}
    </PageContainer>
  );
}

function SistemaContent({ data }: { data: SistemaStatus }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Pill label="Ambiente" value={data.env.toUpperCase()} />
        <Pill label="Versione servizio" value={data.service_version} />
        <Pill label="Migration DB" value={data.alembic_version ?? "—"} />
      </div>

      <Section icon={Server} title="Cloud Run">
        {data.cloud_run.length === 0 ? (
          <EmptyNote text={data.note ?? "Non disponibile."} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.cloud_run.map((s) => (
              <CloudRunCard key={s.name} status={s} />
            ))}
          </div>
        )}
      </Section>

      <Section icon={Database} title="Cloud SQL">
        <CloudSqlCard status={data.cloud_sql} />
      </Section>

      <Section icon={ShieldCheck} title="Certificati SSL">
        {data.ssl_certs.length === 0 ? (
          <EmptyNote text={data.env === "local" ? "Non disponibile in dev." : "Nessun certificato trovato."} />
        ) : (
          <div className="space-y-2">
            {data.ssl_certs.map((c) => (
              <SslCertCard key={c.name} cert={c} />
            ))}
          </div>
        )}
      </Section>

      <p className="text-xs text-muted-foreground text-right">
        Ultimo aggiornamento: {formatDataOra(data.queried_at)}
      </p>
    </div>
  );
}

function Pill({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border bg-card px-4 py-3">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold text-foreground tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

function Section({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h2 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
        <Icon className="size-4 text-muted-foreground" />
        {title}
      </h2>
      {children}
    </section>
  );
}

function EmptyNote({ text }: { text: string }) {
  return (
    <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
      {text}
    </div>
  );
}

function CloudRunCard({ status }: { status: CloudRunStatus }) {
  const ok = status.ready && !status.errore;
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="font-mono text-sm font-semibold text-foreground">{status.name}</div>
        {ok ? (
          <Badge variant="outline" className="text-[10px] bg-success/10 text-success border-success/30">
            <CheckCircle2 className="size-3 mr-1" /> ready
          </Badge>
        ) : (
          <Badge variant="outline" className="text-[10px] bg-destructive/10 text-destructive border-destructive/30">
            <XCircle className="size-3 mr-1" /> errore
          </Badge>
        )}
      </div>
      {status.errore && (
        <p className="text-xs text-destructive mt-1">{status.errore}</p>
      )}
      {status.url && (
        <a
          href={status.url}
          target="_blank"
          rel="noreferrer"
          className="block text-xs text-muted-foreground hover:text-foreground mt-2 truncate"
        >
          <Globe className="size-3 inline mr-1" />
          {status.url}
        </a>
      )}
      {status.image_sha && (
        <div className="text-[11px] text-muted-foreground mt-1">
          Image: <span className="font-mono">{status.image_sha.substring(0, 12)}</span>
        </div>
      )}
      {status.last_deploy_at && (
        <div className="text-[11px] text-muted-foreground mt-0.5">
          Ultimo deploy: {formatDataOra(status.last_deploy_at)}
        </div>
      )}
    </div>
  );
}

function CloudSqlCard({ status }: { status: CloudSqlStatus }) {
  if (status.info) return <EmptyNote text={status.info} />;
  if (status.errore)
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
        {status.errore}
      </div>
    );
  const ok = status.state === "RUNNABLE";
  return (
    <div className="rounded-lg border bg-card p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
      <div>
        <div className="text-xs text-muted-foreground">Istanza</div>
        <div className="font-mono text-sm font-medium">{status.name ?? "—"}</div>
      </div>
      <div>
        <div className="text-xs text-muted-foreground">Stato</div>
        <Badge
          variant="outline"
          className={cn(
            "text-[10px]",
            ok
              ? "bg-success/10 text-success border-success/30"
              : "bg-amber-100 text-amber-900 border-amber-300"
          )}
        >
          {status.state ?? "?"}
        </Badge>
      </div>
      <div>
        <div className="text-xs text-muted-foreground">Versione</div>
        <div className="text-sm font-medium">{status.database_version ?? "—"}</div>
      </div>
      <div>
        <div className="text-xs text-muted-foreground">Tier</div>
        <div className="text-sm font-medium">{status.tier ?? "—"}</div>
      </div>
    </div>
  );
}

function SslCertCard({ cert }: { cert: SslCert }) {
  const ok = cert.status === "ACTIVE";
  return (
    <div className="rounded-lg border bg-card p-3 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="font-mono text-sm font-medium">{cert.name}</div>
        <div className="text-xs text-muted-foreground truncate">
          {cert.domains.join(" · ")}
        </div>
        {cert.expire_time && (
          <div className="text-xs text-muted-foreground mt-0.5">
            Scade: {formatDataOra(cert.expire_time)}
          </div>
        )}
      </div>
      <Badge
        variant="outline"
        className={cn(
          "text-[10px] shrink-0",
          ok
            ? "bg-success/10 text-success border-success/30"
            : "bg-amber-100 text-amber-900 border-amber-300"
        )}
      >
        <Activity className="size-3 mr-1" />
        {cert.status}
      </Badge>
    </div>
  );
}
